import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Is the soft keyboard up? Not "does something have focus" — actually up.
 *
 * The composer gives back its `env(safe-area-inset-bottom)` strip while the keyboard is
 * covering it, so the padding under the field doesn't end up several times the padding above
 * it. That used to be `:focus-within`, and on Android that is a different question with a
 * different answer: dismissing the keyboard with the system back button LEAVES THE FIELD
 * FOCUSED, so the bar kept its padding at 0 and sat in the strip the navigation bar is drawn
 * over — the icons and the field were under the nav buttons and every tap went to the system.
 *
 * Two sources, because neither alone is right everywhere:
 *  - **Native** (`@capacitor/keyboard`): the shell is configured `resize: 'native'`, so the
 *    WebView frame shrinks and visualViewport sees nothing covering it. The plugin's events
 *    are the only honest answer there, on both platforms.
 *  - **Web**: `visualViewport` shrinks while the layout viewport doesn't, so the difference IS
 *    the keyboard. Anything under THRESHOLD is browser chrome (Android Chrome's collapsing
 *    URL bar), not a keyboard.
 *
 * The answer is mirrored onto `<html data-keyboard="up">` so CSS can ask it too (index.css:
 * `.composer-bar`). Ref-counted, so one consumer unmounting doesn't clear another's.
 */
const THRESHOLD = 90;

let openCount = 0;

/** How much of the layout viewport the keyboard is covering, in px (0 when it isn't). */
export function keyboardCoverage(vv = typeof window !== 'undefined' ? window.visualViewport : null) {
  if (!vv) return 0;
  const covered = window.innerHeight - ((vv.offsetTop || 0) + vv.height);
  return covered > THRESHOLD ? Math.round(covered) : 0;
}

function mark(up) {
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  if (!root) return;
  openCount = Math.max(0, openCount + (up ? 1 : -1));
  if (openCount > 0) root.setAttribute('data-keyboard', 'up');
  else root.removeAttribute('data-keyboard');
}

export default function useKeyboardOpen() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let current = false;
    const set = (next) => {
      if (next === current) return;
      current = next;
      mark(next);
      setOpen(next);
    };

    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    const sync = () => set(keyboardCoverage(vv) > 0);
    if (vv) {
      sync();
      vv.addEventListener('resize', sync);
      vv.addEventListener('scroll', sync);
    }

    let detach = null;
    if (Capacitor.isNativePlatform()) {
      (async () => {
        try {
          const { Keyboard } = await import('@capacitor/keyboard');
          const show = await Keyboard.addListener('keyboardWillShow', () => set(true));
          const shown = await Keyboard.addListener('keyboardDidShow', () => set(true));
          const hide = await Keyboard.addListener('keyboardWillHide', () => set(false));
          const hidden = await Keyboard.addListener('keyboardDidHide', () => set(false));
          detach = () => { show.remove(); shown.remove(); hide.remove(); hidden.remove(); };
        } catch {
          // No plugin (web build, or a shell without it) — visualViewport answers alone.
        }
      })();
    }

    return () => {
      if (vv) {
        vv.removeEventListener('resize', sync);
        vv.removeEventListener('scroll', sync);
      }
      detach?.();
      if (current) mark(false);
    };
  }, []);

  return open;
}
